import { gql } from "@apollo/client";
import { client } from "../apolloClient";
import { useQuery, useMutation } from "@apollo/client";

/**
 * useProjectDeliverableApi custom hook for project deliverable operations.
 * - Provides CRUD operations for project deliverables using Apollo Client and GraphQL.
 * - Handles cache updates for project deliverable queries and mutations.
 * - Exposes helpers for reading/writing/updating project deliverables in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting deliverables.
 * - Ensures UI stays in sync with backend by updating Apollo cache after mutations.
 */

// GraphQL Queries and Mutations
const PROJECT_DELIVERABLE_QUERIES = {
  // Query to fetch all deliverables
  GET_PROJECT_DELIVERABLES: gql`
    query GetProjectDeliverables {
      GetProjectDeliverables {
        deliverable_id
        milestone_id
        deliverable_code
        deliverable_name
        deliverable_description
        deliverable_start_date
        deliverable_end_date
        deliverable_status_code
        is_active
        created_on
        updated_on
      }
    }
  `,
  // Query to fetch deliverables by milestone
  GET_PROJECT_DELIVERABLES_BY_MILESTONE_ID: gql`
    query GetProjectDeliverablesByMilestoneId($milestoneId: Int!) {
      GetProjectDeliverablesByMilestoneId(milestone_id: $milestoneId) {
        deliverable_id
        milestone_id
        deliverable_code
        deliverable_name
        deliverable_description
        deliverable_start_date
        deliverable_end_date
        deliverable_status_code
        is_active
        created_on
        updated_on
      }
    }
  `,
  // Mutation to add a deliverable
  ADD_PROJECT_DELIVERABLE: gql`
    mutation AddProjectDeliverable(
      $milestone_id: Int!
      $deliverable_code: String!
      $deliverable_name: String!
      $deliverable_description: String
      $deliverable_status_code: String
      $deliverable_start_date: String
      $deliverable_end_date: String
    ) {
      AddProjectDeliverable(
        milestone_id: $milestone_id
        deliverable_code: $deliverable_code
        deliverable_name: $deliverable_name
        deliverable_description: $deliverable_description
        deliverable_status_code: $deliverable_status_code
        deliverable_start_date: $deliverable_start_date
        deliverable_end_date: $deliverable_end_date
      ) {
        deliverable_id
        milestone_id
        deliverable_code
        deliverable_name
        deliverable_description
        deliverable_start_date
        deliverable_end_date
        deliverable_status_code
        is_active
        created_on
        updated_on
      }
    }
  `,
  // Mutation to update a deliverable
  UPDATE_PROJECT_DELIVERABLE: gql`
    mutation UpdateProjectDeliverable(
      $deliverable_id: ID!
      $milestone_id: Int
      $deliverable_code: String!
      $deliverable_name: String
      $deliverable_description: String
      $deliverable_status_code: String
      $deliverable_start_date: String
      $deliverable_end_date: String
    ) {
      UpdateProjectDeliverable(
        deliverable_id: $deliverable_id
        milestone_id: $milestone_id
        deliverable_code: $deliverable_code
        deliverable_name: $deliverable_name
        deliverable_description: $deliverable_description
        deliverable_status_code: $deliverable_status_code
        deliverable_start_date: $deliverable_start_date
        deliverable_end_date: $deliverable_end_date
      ) {
        deliverable_id
        milestone_id
        deliverable_code
        deliverable_name
        deliverable_description
        deliverable_start_date
        deliverable_end_date
        deliverable_status_code
        is_active
        created_on
        updated_on
      }
    }
  `,
  // Mutation to delete a deliverable
  DELETE_PROJECT_DELIVERABLE: gql`
    mutation DeleteProjectDeliverable($deliverable_id: ID!) {
      DeleteProjectDeliverable(deliverable_id: $deliverable_id) {
        deliverable_id
        milestone_id
        deliverable_code
        deliverable_name
        deliverable_description
        deliverable_status_code
        is_active
        created_on
        updated_on
      }
    }
  `,
};

// Cache operations for project deliverables
const cacheOperations = {
  /**
   * Writes the provided deliverables array to Apollo Client cache.
   * @param {Array} projectDeliverables
   */
  writeProjectDeliverables: (projectDeliverables) => {
    if (!projectDeliverables) return;
    client.writeQuery({
      query: PROJECT_DELIVERABLE_QUERIES.GET_PROJECT_DELIVERABLES,
      data: {
        GetProjectDeliverables: projectDeliverables,
      },
    });
  },

  /**
   * Reads the deliverables array from Apollo Client cache.
   * @returns {Array} Array of deliverable objects or empty array if not found.
   */
  readProjectDeliverables: () => {
    try {
      const { GetProjectDeliverables } =
        client.readQuery({
          query: PROJECT_DELIVERABLE_QUERIES.GET_PROJECT_DELIVERABLES,
        }) || {};
      return GetProjectDeliverables || [];
    } catch {
      return [];
    }
  },

  /**
   * Updates a deliverable in the Apollo Client cache.
   * @param {Object} updatedProjectDeliverable
   */
  updateProjectDeliverablesInCache: (updatedProjectDeliverable) => {
    const existingProjectDeliverables = cacheOperations.readProjectDeliverables();
    const updatedProjectDeliverables = existingProjectDeliverables.map(
      (projectDeliverable) =>
        projectDeliverable.deliverable_id === updatedProjectDeliverable.deliverable_id
          ? updatedProjectDeliverable
          : projectDeliverable
    );
    cacheOperations.writeProjectDeliverables(updatedProjectDeliverables);
  },
};

export const useProjectDeliverableApi = () => {
  const [addProjectDeliverableMutation] = useMutation(
    PROJECT_DELIVERABLE_QUERIES.ADD_PROJECT_DELIVERABLE
  );
  const [updateProjectDeliverableMutation] = useMutation(
    PROJECT_DELIVERABLE_QUERIES.UPDATE_PROJECT_DELIVERABLE
  );

  /**
   * Fetches all project deliverables.
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getProjectDeliverables = async () => {
    try {
      const { data } = await client.query({
        query: PROJECT_DELIVERABLE_QUERIES.GET_PROJECT_DELIVERABLES,
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetProjectDeliverables };
    } catch (error) {
      console.error("Error fetching GetProjectDeliverables:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches a project deliverable by its ID.
   * @param {number|string} deliverable_id
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const getProjectDeliverableById = async (deliverable_id) => {
    try {
      const { data } = await client.query({
        query: PROJECT_DELIVERABLE_QUERIES.GET_PROJECT_DELIVERABLE_BY_ID,
        variables: { deliverable_id },
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetProjectDeliverableById };
    } catch (error) {
      console.error("Error fetching project deliverable by ID:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches project deliverables by milestone ID.
   * @param {number|string} milestone_id
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getProjectDeliverablesByMilestoneId = async (milestone_id) => {
    try {
      const { data } = await client.query({
        query: PROJECT_DELIVERABLE_QUERIES.GET_PROJECT_DELIVERABLES_BY_MILESTONE_ID,
        variables: { milestoneId: parseInt(milestone_id) },
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetProjectDeliverablesByMilestoneId };
    } catch (error) {
      console.error("Error fetching project deliverable by milestone:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Creates a new project deliverable.
   * @param {Object} formData
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const createProjectDeliverable = async (formData) => {
    try {
      const { data, errors } = await addProjectDeliverableMutation({
        variables: {
          milestone_id: parseInt(formData.milestone_id),
          deliverable_code: formData.deliverable_code,
          deliverable_name: formData.deliverable_name,
          deliverable_description: formData.deliverable_description,
          deliverable_status_code: formData.deliverable_status_code,
          deliverable_start_date: formData.deliverable_start_date,
          deliverable_end_date: formData.deliverable_end_date,
        },
      });

      if (errors?.length > 0) {
        throw new Error(errors[0].message);
      }

      if (!data?.AddProjectDeliverable) {
        throw new Error("Failed to create project deliverable");
      }

      return {
        success: true,
        data: data.AddProjectDeliverable,
      };
    } catch (error) {
      // Handle GraphQL errors
      if (error.graphQLErrors?.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        throw new Error(graphQLError.message);
      }

      // Handle network errors
      if (error.networkError?.result?.errors?.length > 0) {
        const networkError = error.networkError.result.errors[0];
        throw new Error(networkError.message);
      }

      throw error;
    }
  };

  /**
   * Updates an existing project deliverable.
   * @param {number|string} deliverable_id
   * @param {Object} formData
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateProjectDeliverable = async (deliverable_id, formData) => {
    try {
      const { data, errors } = await updateProjectDeliverableMutation({
        variables: {
          deliverable_id: parseInt(deliverable_id),
          milestone_id: parseInt(formData.milestone_id),
          deliverable_code: formData.deliverable_code,
          deliverable_name: formData.deliverable_name,
          deliverable_description: formData.deliverable_description,
          deliverable_status_code: formData.deliverable_status_code,
          deliverable_start_date: formData.deliverable_start_date,
          deliverable_end_date: formData.deliverable_end_date,
        },
      });

      if (errors?.length > 0) {
        throw new Error(errors[0].message);
      }

      if (!data?.UpdateProjectDeliverable) {
        throw new Error("Failed to update ProjectDeliverable");
      }

      return {
        success: true,
        data: data.UpdateProjectDeliverable,
      };
    } catch (error) {
      // Handle GraphQL errors
      if (error.graphQLErrors?.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        throw new Error(graphQLError.message);
      }

      // Handle network errors
      if (error.networkError?.result?.errors?.length > 0) {
        const networkError = error.networkError.result.errors[0];
        throw new Error(networkError.message);
      }

      throw error;
    }
  };

  /**
   * Deletes a project deliverable by its ID.
   * @param {number|string} deliverableId
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const deleteProjectDeliverable = async (deliverableId) => {
    try {
      const { data } = await client.mutate({
        mutation: PROJECT_DELIVERABLE_QUERIES.DELETE_PROJECT_DELIVERABLE,
        variables: { deliverable_id: parseInt(deliverableId) },
      });

      const existingProjectDeliverables = cacheOperations.readProjectDeliverables();
      cacheOperations.writeProjectDeliverables(
        existingProjectDeliverables.filter(
          (projectDeliverable) => projectDeliverable.deliverable_id !== deliverableId
        )
      );

      return { success: true, data: data.DeleteProjectDeliverable };
    } catch (error) {
      console.error("Error deleting ProjectDeliverable:", error);
      return { success: false, error: error.message };
    }
  };

  return {
    getProjectDeliverables,
    getProjectDeliverableById,
    createProjectDeliverable,
    updateProjectDeliverable,
    deleteProjectDeliverable,
    getProjectDeliverablesByMilestoneId,
    cacheOperations,
  };
};
